import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useCustomers } from '../../context/customers';
import { useClients } from '../../context/clients';
import { useBranches } from '../../context/branches';
import { useDrivers } from '../../context/drivers';
import { useVehicles, useVehicleTypes } from '../../context/vehicles';
import { useTripTypes, useTripStatuses } from '../../context/trips';
import { USER_PROFILE, USER_ROLE } from '../../services/auth';

const ReportHeader = ({
  fromDate, toDate, selectedCustomer, selectedClient, search,
  filters,
  onFromDateSelected, onToDateSelected, onCustomerChange, onClientChange,
  onSearch, onFilterChange, onClearFilters,
  handleViewReports, handleDownload, downloadLoading,
}) => {
  const role      = localStorage.getItem(USER_ROLE);
  const profile   = JSON.parse(localStorage.getItem(USER_PROFILE) || '{}');
  const isAdmin   = role === 'admin';
  const branchId  = Number(profile?.branch?.id || profile?.branch || 0);
  const customerParams = isAdmin
    ? { status: 'active', paginate: false }
    : { status: 'active', paginate: false, branch: branchId };

  const { customers } = useCustomers({ reload: true, params: customerParams });
  const { clients }   = useClients({ reload: true, params: { status: 'active', paginate: false } });

  return (
    <div className="bg-white p-4 rounded shadow mb-4 space-y-4">
      {/* ── Top row: date range + customer + trip code ── */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col min-w-[130px]">
          <label className="text-xs text-gray-500 mb-1">From Date</label>
          <input type="date" value={fromDate} onChange={onFromDateSelected} className="border rounded p-2 text-sm" />
        </div>
        <div className="flex flex-col min-w-[130px]">
          <label className="text-xs text-gray-500 mb-1">To Date</label>
          <input type="date" value={toDate} onChange={onToDateSelected} className="border rounded p-2 text-sm" />
        </div>
        <div className="flex flex-col min-w-[150px]">
          <label className="text-xs text-gray-500 mb-1">Customer</label>
          <select value={selectedCustomer} onChange={onCustomerChange} className="border px-2 py-2 text-sm rounded uppercase">
            <option value="all">All</option>
            {customers.map(c => (
              <option key={c.id} value={c.id}>{(c.name || '').toUpperCase()}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col min-w-[150px]">
          <label className="text-xs text-gray-500 mb-1">Client</label>
          <select value={selectedClient} onChange={onClientChange} className="border px-2 py-2 text-sm rounded uppercase">
            <option value="all">All</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{(c.name || '').toUpperCase()}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col min-w-[160px]">
          <label className="text-xs text-gray-500 mb-1">Trip Code</label>
          <input
            type="text" value={search} onChange={onSearch}
            placeholder="Enter trip code"
            className="border rounded p-2 text-sm uppercase"
          />
        </div>
      </div>

      {/* ── Filters — always visible ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
          <button onClick={onClearFilters} className="text-xs text-red-500 hover:underline">
            Clear all filters
          </button>
        </div>
        <ReportFilters filters={filters} onFilterChange={onFilterChange} onClearFilters={onClearFilters} />
      </div>

      {/* ── Hint + action buttons at the bottom ── */}
      <div className="border-t pt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 italic">
          Adjust the filters above, then click <strong>View Reports</strong> to load the updated results.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleViewReports} className="bg-blue-600 text-white px-4 py-2 rounded text-sm whitespace-nowrap">
            View Reports
          </button>
          {role !== 'branch_user' && (
            <button
              onClick={handleDownload}
              disabled={downloadLoading}
              className="bg-green-600 text-white px-4 py-2 rounded text-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {downloadLoading ? 'Downloading...' : 'Download'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportHeader;


/* ─── Stable date input that doesn't drop focus mid-year-typing ────────────
 *
 * The root cause of the focus-loss bug:
 *   1. `DateField` was defined inside `ReportFilters`, so a new function ref
 *      was created on every render → React unmounted/remounted the input.
 *   2. `onChange` fired after each keystroke and updated parent state, which
 *      triggered a re-render while the year field was only partially typed.
 *
 * Fix:
 *   - Define `DateField` at module level (stable identity across renders).
 *   - Buffer the raw value in local state.
 *   - Only call `onCommit` (parent state update) when the browser reports a
 *     complete, valid date — i.e. the value matches YYYY-MM-DD with a
 *     4-digit year.  Partial values (year still being typed) are kept local.
 *   - `onBlur` also commits so clicking away with a valid partial value
 *     still saves it.
 * ─────────────────────────────────────────────────────────────────────── */
function DateField({ label, filterKey, value, onCommit }) {
  const [local, setLocal] = useState(value ?? '');

  // Keep local in sync when the parent clears the filter externally.
  // Using a simple comparison avoids resetting while the user is typing.
  if (value === '' && local !== '') {
    setLocal('');
  }

  const isComplete = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);

  const handleChange = (e) => {
    const v = e.target.value;
    setLocal(v);
    if (isComplete(v)) {
      onCommit({ target: { value: v } }, filterKey);
    }
  };

  const handleBlur = (e) => {
    const v = e.target.value;
    if (isComplete(v)) {
      onCommit({ target: { value: v } }, filterKey);
    }
  };

  const handleClear = () => {
    setLocal('');
    onCommit({ target: { value: '' } }, filterKey);
  };

  return (
    <div className="flex flex-col">
      <label className="text-xs text-gray-600 mb-0.5">{label}</label>
      <div className="relative">
        <input
          type="date"
          value={local}
          onChange={handleChange}
          onBlur={handleBlur}
          className="border px-2 py-1 text-sm rounded w-full"
        />
        {local && (
          <XMarkIcon
            onClick={handleClear}
            className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"
            style={{ width: 14, height: 14 }}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Filters panel (mirrors TripFilters) ─────────────────────────────────── */
export const ReportFilters = ({ filters, onFilterChange, onClearFilters }) => {
  const role = localStorage.getItem(USER_ROLE);
  const isAdmin = role === 'admin';

  let branches = [];
  if (isAdmin) {
    const result = useBranches({ reload: false, params: { status: 'active', paginate: false } });
    branches = result.branches;
  }

  const { drivers }     = useDrivers({ reload: false, params: { status: 'active', paginate: false } });
  const { vehicles }    = useVehicles({ reload: false, params: { status: 'active', paginate: false } });
  const { vehicleTypes } = useVehicleTypes({ reload: false });
  const { tripTypes }   = useTripTypes({ reload: false });
  const { tripStatuses: statuses } = useTripStatuses({ reload: false });

  const [selectedBranch, setSelectedBranch] = useState(filters.branch || 0);

  const filteredDrivers  = (isAdmin && selectedBranch) ? drivers.filter(d => d.branch?.id === Number(selectedBranch)) : drivers;
  const filteredVehicles = (isAdmin && selectedBranch) ? vehicles.filter(v => v.branch?.id === Number(selectedBranch)) : vehicles;

  const onBranchChange = e => {
    setSelectedBranch(e.target.value);
    onFilterChange(e, 'branch');
  };

  return (
    <div className="border-t pt-4 mt-2">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Trip Type */}
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-0.5">Trip Type</label>
          <select value={filters.trip_type} onChange={e => onFilterChange(e, 'trip_type')} className="border px-2 py-1 text-sm rounded">
            <option value="">All</option>
            {tripTypes.map(tt => <option key={tt.id} value={tt.id}>{tt.value}</option>)}
          </select>
        </div>

        {/* Status */}
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-0.5">Trip Status</label>
          <select value={filters.trip_status} onChange={e => onFilterChange(e, 'trip_status')} className="border px-2 py-1 text-sm rounded">
            <option value="">All</option>
            {statuses.map(s => <option key={s.id} value={s.id}>{s.value}</option>)}
          </select>
        </div>

        {/* Branch */}
        {isAdmin && (
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-0.5">Branch</label>
            <select value={filters.branch} onChange={onBranchChange} className="border px-2 py-1 text-sm rounded">
              <option value="">All</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}

        {/* Driver */}
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-0.5">Driver</label>
          <select value={filters.driver} onChange={e => onFilterChange(e, 'driver')} className="border px-2 py-1 text-sm rounded">
            <option value="">All</option>
            {filteredDrivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>

        {/* Assigned Vehicle */}
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-0.5">Assigned Vehicle</label>
          <select value={filters.assigned_vehicle} onChange={e => onFilterChange(e, 'assigned_vehicle')} className="border px-2 py-1 text-sm rounded">
            <option value="">All</option>
            {filteredVehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
          </select>
        </div>

        {/* Requested Vehicle (type) */}
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-0.5">Requested Vehicle</label>
          <select value={filters.requested_vehicle} onChange={e => onFilterChange(e, 'requested_vehicle')} className="border px-2 py-1 text-sm rounded">
            <option value="">All</option>
            {vehicleTypes.map(vt => <option key={vt.id} value={vt.id}>{vt.name}</option>)}
          </select>
        </div>

        {/* Date fields */}
        <DateField label="Booking Date"           filterKey="booking_date"           value={filters.booking_date}           onCommit={onFilterChange} />
        <DateField label="Placement Date"         filterKey="placement_pickup_date"  value={filters.placement_pickup_date}  onCommit={onFilterChange} />
        <DateField label="Expected Pickup Date"   filterKey="expected_pickup_date"   value={filters.expected_pickup_date}   onCommit={onFilterChange} />
        <DateField label="Pickup In Date"         filterKey="actual_in_pickup_date"  value={filters.actual_in_pickup_date}  onCommit={onFilterChange} />
        <DateField label="Pickup Out Date"        filterKey="actual_out_pickup_date" value={filters.actual_out_pickup_date} onCommit={onFilterChange} />
        <DateField label="Expected Delivery Date" filterKey="expected_delivery_date" value={filters.expected_delivery_date} onCommit={onFilterChange} />
        <DateField label="Delivery In Date"       filterKey="actual_in_delivery_date"  value={filters.actual_in_delivery_date}  onCommit={onFilterChange} />
        <DateField label="Delivery Out Date"      filterKey="actual_out_delivery_date" value={filters.actual_out_delivery_date} onCommit={onFilterChange} />
      </div>

      <div className="mt-3" />
    </div>
  );
};
